
import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { UserCog, Trash2, ArchiveRestore } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useArchiveUser } from "@/hooks/use-archive-user";
import { useAuth } from "@/contexts/AuthContext";
import { UserRoleEdit } from "./UserRoleEdit";
import { SimplifiedUserRole } from "@/types/user";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface UserRoleRowProps {
  role: SimplifiedUserRole;
  restrictToOrgAdmin?: boolean;
}

export function UserRoleRow({ role, restrictToOrgAdmin = false }: UserRoleRowProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { archiveUser, restoreUser } = useArchiveUser();
  const [reason, setReason] = useState("");
  const isArchived = role.role === 'archived';
  const isSelf = user?.id === role.user_id;
  const profileId = role.profiles?.id || null;
  const canArchive = !isSelf && !!profileId;

  const handleUpdateUser = async (userId: string, formData: any) => {
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          first_name: formData.first_name,
          last_name: formData.last_name,
        })
        .eq('user_id', userId);

      if (profileError) throw profileError;

      const { error: roleError } = await supabase
        .from('user_roles')
        .update({ 
          role: formData.role,
          organization_id: formData.organization_id 
        })
        .eq('user_id', userId);

      if (roleError) throw roleError;

      await queryClient.invalidateQueries({ queryKey: ["user-roles"] });
      toast.success('User details updated successfully');
    } catch (error) {
      console.error('Error updating user details:', error);
      toast.error('Failed to update user details');
    }
  };

  const handleArchive = () => {
    if (!profileId) return;
    archiveUser.mutate(
      { contactId: profileId, userId: role.user_id, reason: reason.trim() || undefined },
      { onSuccess: () => { setReason(""); queryClient.invalidateQueries({ queryKey: ["user-roles"] }); } }
    );
  };

  const handleRestore = () => {
    if (!profileId) return;
    restoreUser.mutate(
      { contactId: profileId, userId: role.user_id },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: ["user-roles"] }) }
    );
  };

  return (
    <TableRow>
      <TableCell className="min-w-[120px] sm:w-[150px] text-left">
        {role.profiles?.first_name || role.profiles?.last_name 
          ? `${role.profiles.first_name || ""} ${role.profiles.last_name || ""}`.trim()
          : "-"
        }
      </TableCell>
      <TableCell className="min-w-[140px] sm:w-[200px] text-left hidden sm:table-cell">{role.organizations?.name || "-"}</TableCell>
      <TableCell className="min-w-[100px] sm:w-[120px] text-left">{role.role}</TableCell>
      <TableCell className="min-w-[160px] sm:w-[180px] text-left">
        <div className="flex items-center gap-1">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm">
              <UserCog className="h-4 w-4" />
              <span className="sr-only">Edit User</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-md mx-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle>Edit User Details</DialogTitle>
            </DialogHeader>
            <UserRoleEdit 
              userId={role.user_id}
              defaultValues={{
                first_name: role.profiles?.first_name || '',
                last_name: role.profiles?.last_name || '',
                organization_id: role.organization_id || '',
                role: role.role as "standard" | "admin" | "superuser" | "partner" | "archived" | "internal_staff" | "advisor" | "production"
              }}
              onSubmit={(values) => handleUpdateUser(role.user_id, values)}
              isLoading={false}
              restrictToOrgAdmin={restrictToOrgAdmin}
            />
          </DialogContent>
        </Dialog>

        {canArchive && !isArchived && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={archiveUser.isPending}>
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Archive User</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this user?</AlertDialogTitle>
                <AlertDialogDescription>
                  They will be signed out and unable to log in. Their previous role is preserved and can be restored later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <Label htmlFor="archive-reason">Reason (optional)</Label>
                <Input
                  id="archive-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Left the company"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setReason("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archiveUser.isPending}>
                  Archive
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {canArchive && isArchived && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={restoreUser.isPending}>
                <ArchiveRestore className="h-4 w-4" />
                <span className="sr-only">Restore User</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Restore this user?</AlertDialogTitle>
                <AlertDialogDescription>
                  Their previous role will be restored and they will regain access to log in.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleRestore} disabled={restoreUser.isPending}>
                  Restore
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        </div>
      </TableCell>
    </TableRow>
  );
}
